import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { reduxForm, InjectedFormProps, formValueSelector } from "redux-form";
import { useLocation, useNavigate } from "react-router";

import { queryString } from "capitalrx-utilities";
import { SubmitBtn } from "capitalrx-components";
import GeneralClaimInformation from "@/App/pages/ManualClaim/GeneralInformation";
import ManualClaimImageContainer from "@/App/components/ManualClaimImageContainer";
import ManualClaimSectionContainer from "@/App/components/ManualClaimSectionContainer";
import { ReactComponent as PatientSearchImage } from "@/images/patient-search.svg";
import { ReactComponent as PharmacySearchImage } from "@/images/pharmacy-search.svg";
import { ReactComponent as DrugSearchImage } from "@/images/drug-search.svg";
import PatientSummary from "./PatientSummary";
import PharmacySummary from "./PharmacySummary";
import DrugSummary from "./DrugSummary";
import { transmissionManualActions } from "@/App/actions/";
import { RootState } from "@/App/reducers";
import { TransmissionTypes } from "@/types";
import { logEvent, ANALYTIC_EVENTS } from "@/App/utils/analytics";

type transmissionOptions = {
  [key: string]: string | number;
};

const formName = "ManualClaim";

const generateRandomPSRN = (): string => {
  const randomNumber = Math.floor(Math.random() * 1000000000000);
  return randomNumber.toString().padStart(12, "0");
};

const ManualClaimForm: React.FC<InjectedFormProps> = (props: InjectedFormProps) => {
  const { submitting, handleSubmit } = props;
  const { search } = useLocation();
  const { member_id, npi, drugs, date_of_service, claim_submission_date } = queryString.getQueryParams(search);

  const dispatch = useDispatch();
  const { submitTransmissionManual } = transmissionManualActions;

  const navigate = useNavigate();

  const handleAmplitudeLogging: Function = (amplitudeTag: string): void => {
    logEvent(amplitudeTag);
  };

  const handleFormSubmit = (data: TransmissionTypes.TransmissionManualFormData): void => {
    let optionalParams = {};
    if (data.days_supply) optionalParams = { ...optionalParams, days_supply: parseInt(data.days_supply) };
    if (data.daw) optionalParams = { ...optionalParams, daw: data.daw };
    if (data.fill_number) optionalParams = { ...optionalParams, fill_number: parseInt(data.fill_number) };
    if (data.prescription_service_reference_number)
      optionalParams = { ...optionalParams, prescription_service_reference_number: data.prescription_service_reference_number };
    if (data.submission_clarification_code_1)
      optionalParams = { ...optionalParams, submission_clarification_code_1: data.submission_clarification_code_1 };
    if (data.diagnosis_code_qualifier_1)
      optionalParams = { ...optionalParams, diagnosis_code_qualifier_1: data.diagnosis_code_qualifier_1 };
    if (data.diagnosis_code_1)
      optionalParams = { ...optionalParams, diagnosis_code_1: data.diagnosis_code_1 };

    if (data.dmr_pay_to_street_address_line_1)
      optionalParams = { ...optionalParams, dmr_pay_to_street_address_line_1: data.dmr_pay_to_street_address_line_1 };
    if (data.dmr_pay_to_street_address_line_2)
      optionalParams = { ...optionalParams, dmr_pay_to_street_address_line_2: data.dmr_pay_to_street_address_line_2 };
    if (data.dmr_pay_to_city_address) optionalParams = { ...optionalParams, dmr_pay_to_city_address: data.dmr_pay_to_city_address };
    if (data.dmr_pay_to_state_province_address)
      optionalParams = { ...optionalParams, dmr_pay_to_state_province_address: data.dmr_pay_to_state_province_address };
    if (data.dmr_pay_to_zip_postal_code)
      optionalParams = { ...optionalParams, dmr_pay_to_zip_postal_code: data.dmr_pay_to_zip_postal_code };

    if (data.prescriber_id_qualifier) optionalParams = { ...optionalParams, prescriber_id_qualifier: data.prescriber_id_qualifier };
    if (data.prescriber_id) optionalParams = { ...optionalParams, prescriber_id: data.prescriber_id };
    if (data.pharmacy_service_type) optionalParams = { ...optionalParams, pharmacy_service_type: data.pharmacy_service_type };
    if (data.place_of_service) optionalParams = { ...optionalParams, place_of_service: data.place_of_service };
    if (data.patient_residence) optionalParams = { ...optionalParams, patient_residence: data.patient_residence };
    if (data.prescription_origin_code) optionalParams = { ...optionalParams, prescription_origin_code: data.prescription_origin_code };
    if (data.tax_id_number) optionalParams = { ...optionalParams, tax_id_number: data.tax_id_number };
    if (data.notes) optionalParams = { ...optionalParams, notes: data.notes };

    if (data.other_coverage_code) optionalParams = { ...optionalParams, other_coverage_code: data.other_coverage_code };
    if (data.other_payer_amount_paid_1) {
      optionalParams = {
        ...optionalParams,
        other_payer_amount_paid_1: data.other_payer_amount_paid_1,
        other_payer_amount_paid_qualifier_1: "07",
      };
    }
    if (data.other_payer_patient_responsibility_amount_1) {
      optionalParams = {
        ...optionalParams,
        other_payer_patient_responsibility_amount_1: data.other_payer_patient_responsibility_amount_1,
        other_payer_patient_responsibility_amount_qualifier_1: "06",
      };
    }
    if (data.other_payer_reject_code_1) optionalParams = { ...optionalParams, other_payer_reject_code_1: data.other_payer_reject_code_1 };

    if (data.special_packaging_indicator) optionalParams = { ...optionalParams, special_packaging_indicator: data.special_packaging_indicator };

    if (data.result_of_service_code) {
      const result_of_service_codes = data.result_of_service_code.split(",");
      for (let index = 1; index <= result_of_service_codes.length; index++) {
        // NCPDP standard only supports 3
        if (index > 3) break;
        // NCPDP doesn't 0 index their shit
        optionalParams[`result_of_service_code_${index}`] = result_of_service_codes[index - 1].trim();
      }
    }

    if (data.reason_for_service_code) {
      const reason_for_service_codes = data.reason_for_service_code.split(",");
      for (let index = 1; index <= reason_for_service_codes.length; index++) {
        // NCPDP standard only supports 3
        if (index > 3) break;
        // NCPDP doesn't 0 index their shit
        optionalParams[`reason_for_service_code_${index}`] = reason_for_service_codes[index - 1].trim();
      }
    }

    if (data.dur_pps_level_of_effort) {
      const dur_pps_level_of_efforts = data.dur_pps_level_of_effort.split(",");
      for (let index = 1; index <= dur_pps_level_of_efforts.length; index++) {
        // NCPDP standard only supports 9
        if (index > 9) break;
        // NCPDP doesn't 0 index their shit
        optionalParams[`dur_pps_level_of_effort_${index}`] = dur_pps_level_of_efforts[index - 1].trim();
      }
    }

    if (data.professional_service_code) {
      const professional_service_codes = data.professional_service_code.split(",");
      for (let index = 1; index <= professional_service_codes.length; index++) {
        // NCPDP standard only supports 9
        if (index > 9) break;
        // NCPDP doesn't 0 index their shit
        optionalParams[`professional_service_code_${index}`] = professional_service_codes[index - 1].trim();
      }
    }

    let transmissionData = {
      bin_number: data.plan.bin_number,
      processor_control_number: data.plan.processor_control_number,
      service_provider_id: data.pharmacy.details.npi,
      service_provider_id_qualifier: "01",
      date_of_service: data.date_of_service,
      claim_submission_date: data.claim_submission_date,
      submitted_cardholder_id: data.details.external_member_id,
      group_id: data.plan.group_number,
      external_group_id: data.group.external_group_id,
      person_code: data.details.person_code,
      date_of_birth: data.details.birth_date,
      patient_gender_code: data.details.sex === "M" ? "1" : "2",
      patient_first_name: data.details.first_name,
      patient_last_name: data.details.last_name,
      member_id: data.details.id.toString(),
      source: data.source,
      direct_member_reimbursement_indicator: data.direct_member_reimbursement_indicator,
      usual_and_customary_charge: data.usual_and_customary_charge,
      prescription_service_reference_number: data.prescription_service_reference_number,
      ...optionalParams,
    } as TransmissionTypes.TransmissionData;

    /* 5 matches the value of the vaccine option in dmrOptions which is the only time
       incentive amount submitted should be used at the moment */
    if (data.direct_member_reimbursement_indicator === "5") {
      transmissionData.incentive_amount_submitted = data.incentive_amount_submitted;
      transmissionData.ingredient_cost_submitted = data.ingredient_cost_submitted;
      transmissionData.dispensing_fee_submitted = data.dispensing_fee_submitted;
      transmissionData.prescription_service_reference_number = generateRandomPSRN();
    }

    if (data.drugs.length === 1) {
      // handle single drug
      transmissionData.compound_code = "1";
      transmissionData.ndc = data.drugs[0].ndc;
      transmissionData.quantity_dispensed = data.drugs[0].quantity;
    } else if (data.drugs.length > 1) {
      // handle compound
      transmissionData.product_service_id_qualifier = "00";
      transmissionData.compound_code = "2";
      transmissionData.ndc = "0";
      transmissionData.compound_ingredient_component_count = data.drugs.length;

      const compoundFields = {} as transmissionOptions;

      data.drugs.forEach((drug, index) => {
        const drugIndex = index + 1;
        compoundFields["compound_product_id_" + drugIndex] = drug.ndc;
        compoundFields["compound_product_id_qualifier_" + drugIndex] = "03";
        compoundFields["compound_ingredient_quantity_" + drugIndex] = drug.quantity;
      });

      transmissionData = { ...transmissionData, ...compoundFields };
    }

    transmissionData.submitted_overrides = JSON.stringify([
      {
        type: "suppress_rejects",
        value: "79, 81, 88",
      },
    ]);

    if (data.prescription_service_reference_number) {
      handleAmplitudeLogging(ANALYTIC_EVENTS.MANUAL_CLAIM.serviceReferenceNumberFilled);
    }
    handleAmplitudeLogging(ANALYTIC_EVENTS.MANUAL_CLAIM.manualClaimAdjudicate);

    dispatch(submitTransmissionManual(transmissionData, "ManualClaim"));
  };

  const selector = formValueSelector(formName);
  const checkFormValues = useSelector((state: RootState) => {
    return {
      days_supply: selector(state, "days_supply"),
      fill_number: selector(state, "fill_number"),
      daw: selector(state, "daw"),
      prescription_service_reference_number: selector(state, "prescription_service_reference_number"),
      date_of_service: selector(state, "date_of_service"),
      claim_submission_date: selector(state, "claim_submission_date"),
      source: selector(state, "source"),
      direct_member_reimbursement_indicator: selector(state, "direct_member_reimbursement_indicator"),
      incentive_amount_submitted: selector(state, "incentive_amount_submitted"),
      usual_and_customary_charge: selector(state, "usual_and_customary_charge"),
      ingredient_cost_submitted: selector(state, "ingredient_cost_submitted"),
      dispensing_fee_submitted: selector(state, "dispensing_fee_submitted"),
      dmr_pay_to_street_address_line_1: selector(state, "dmr_pay_to_street_address_line_1"),
      dmr_pay_to_street_address_line_2: selector(state, "dmr_pay_to_street_address_line_2"),
      dmr_pay_to_city_address: selector(state, "dmr_pay_to_city_address"),
      dmr_pay_to_state_province_address: selector(state, "dmr_pay_to_state_province_address"),
      dmr_pay_to_zip_postal_code: selector(state, "dmr_pay_to_zip_postal_code"),
      submission_clarification_code_1: selector(state, "submission_clarification_code_1"),
      diagnosis_code_qualifier_1: selector(state, "diagnosis_code_qualifier_1"),
      diagnosis_code_1: selector(state, "diagnosis_code_1"),
      professional_service_code: selector(state, "professional_service_code"),
      dur_pps_level_of_effort: selector(state, "dur_pps_level_of_effort"),
      reason_for_service_code: selector(state, "reason_for_service_code"),
      result_of_service_code: selector(state, "result_of_service_code"),
      prescriber_id_qualifier: selector(state, "prescriber_id_qualifier"),
      prescriber_id: selector(state, "prescriber_id"),
      pharmacy_service_type: selector(state, "pharmacy_service_type"),
      place_of_service: selector(state, "place_of_service"),
      patient_residence: selector(state, "patient_residence"),
      prescription_origin_code: selector(state, "prescription_origin_code"),
      other_coverage_code: selector(state, "other_coverage_code"),
      other_payer_amount_paid_1: selector(state, "other_payer_amount_paid_1"),
      other_payer_patient_responsibility_amount_1: selector(state, "other_payer_patient_responsibility_amount_1"),
      other_payer_reject_code_1: selector(state, "other_payer_reject_code_1"),
      special_packaging_indicator: selector(state, "special_packaging_indicator"),
      notes: selector(state, "notes"),
    };
  });

  const handleOnBlur = (): void => {
    const queryParams = queryString.getQueryParams(search);

    if (checkFormValues.date_of_service || queryParams.date_of_service) {
      queryParams.date_of_service = checkFormValues.date_of_service;
    } else {
      delete checkFormValues["date_of_service"];
    }

    if (checkFormValues.claim_submission_date || queryParams.claim_submission_date) {
      queryParams.claim_submission_date = checkFormValues.claim_submission_date;
    } else {
      delete checkFormValues["claim_submission_date"];
    }

    if (checkFormValues.prescription_service_reference_number || queryParams.prescription_service_reference_number) {
      queryParams.prescription_service_reference_number = checkFormValues.prescription_service_reference_number;
    } else {
      delete checkFormValues["prescription_service_reference_number"];
    }

    if (checkFormValues.days_supply || queryParams.days_supply) {
      queryParams.days_supply = checkFormValues.days_supply;
    } else {
      delete checkFormValues["days_supply"];
    }

    if (checkFormValues.fill_number || queryParams.fill_number) {
      queryParams.fill_number = checkFormValues.fill_number;
    } else {
      delete checkFormValues["fill_number"];
    }

    if (checkFormValues.daw || queryParams.daw) {
      queryParams.daw = checkFormValues.daw;
    } else {
      delete checkFormValues["daw"];
    }

    if (checkFormValues.source || queryParams.source) {
      queryParams.source = checkFormValues.source;
    } else {
      delete checkFormValues["source"];
    }

    if (checkFormValues.direct_member_reimbursement_indicator || queryParams.direct_member_reimbursement_indicator) {
      queryParams.direct_member_reimbursement_indicator = checkFormValues.direct_member_reimbursement_indicator;
    } else {
      delete checkFormValues["direct_member_reimbursement_indicator"];
    }

    if (checkFormValues.usual_and_customary_charge || queryParams.usual_and_customary_charge) {
      queryParams.usual_and_customary_charge = checkFormValues.usual_and_customary_charge;
    } else {
      delete checkFormValues["usual_and_customary_charge"];
    }

    if (checkFormValues.ingredient_cost_submitted || queryParams.ingredient_cost_submitted) {
      queryParams.ingredient_cost_submitted = checkFormValues.ingredient_cost_submitted;
    } else {
      delete checkFormValues["ingredient_cost_submitted"];
    }

    if (checkFormValues.dispensing_fee_submitted || queryParams.dispensing_fee_submitted) {
      queryParams.dispensing_fee_submitted = checkFormValues.dispensing_fee_submitted;
    } else {
      delete checkFormValues["dispensing_fee_submitted"];
    }

    if (checkFormValues.incentive_amount_submitted || queryParams.incentive_amount_submitted) {
      queryParams.incentive_amount_submitted = checkFormValues.incentive_amount_submitted;
    } else {
      delete checkFormValues["incentive_amount_submitted"];
    }

    if (checkFormValues.dmr_pay_to_street_address_line_1 || queryParams.dmr_pay_to_street_address_line_1) {
      queryParams.dmr_pay_to_street_address_line_1 = checkFormValues.dmr_pay_to_street_address_line_1;
    } else {
      delete checkFormValues["dmr_pay_to_street_address_line_1"];
    }

    if (checkFormValues.dmr_pay_to_street_address_line_2 || queryParams.dmr_pay_to_street_address_line_2) {
      queryParams.dmr_pay_to_street_address_line_2 = checkFormValues.dmr_pay_to_street_address_line_2;
    } else {
      delete checkFormValues["dmr_pay_to_street_address_line_2"];
    }

    if (checkFormValues.dmr_pay_to_city_address || queryParams.dmr_pay_to_city_address) {
      queryParams.dmr_pay_to_city_address = checkFormValues.dmr_pay_to_city_address;
    } else {
      delete checkFormValues["dmr_pay_to_city_address"];
    }

    if (checkFormValues.dmr_pay_to_state_province_address || queryParams.dmr_pay_to_state_province_address) {
      queryParams.dmr_pay_to_state_province_address = checkFormValues.dmr_pay_to_state_province_address;
    } else {
      delete checkFormValues["dmr_pay_to_state_province_address"];
    }

    if (checkFormValues.dmr_pay_to_zip_postal_code || queryParams.dmr_pay_to_zip_postal_code) {
      queryParams.dmr_pay_to_zip_postal_code = checkFormValues.dmr_pay_to_zip_postal_code;
    } else {
      delete checkFormValues["dmr_pay_to_zip_postal_code"];
    }

    if (checkFormValues.submission_clarification_code_1 || queryParams.submission_clarification_code_1) {
      queryParams.submission_clarification_code_1 = checkFormValues.submission_clarification_code_1;
    } else {
      delete checkFormValues["submission_clarification_code_1"];
    }

    if (checkFormValues.diagnosis_code_qualifier_1 || queryParams.diagnosis_code_qualifier_1) {
      queryParams.diagnosis_code_qualifier_1 = checkFormValues.diagnosis_code_qualifier_1;
    } else {
      delete checkFormValues["diagnosis_code_qualifier_1"];
    }

    if (checkFormValues.diagnosis_code_1 || queryParams.diagnosis_code_1) {
      queryParams.diagnosis_code_1 = checkFormValues.diagnosis_code_1;
    } else {
      delete checkFormValues["diagnosis_code_1"];
    }

    if (checkFormValues.patient_residence || queryParams.patient_residence) {
      queryParams.patient_residence = checkFormValues.patient_residence;
    } else {
      delete checkFormValues["patient_residence"];
    }

    if (checkFormValues.place_of_service || queryParams.place_of_service) {
      queryParams.place_of_service = checkFormValues.place_of_service;
    } else {
      delete checkFormValues["place_of_service"];
    }

    if (checkFormValues.pharmacy_service_type || queryParams.pharmacy_service_type) {
      queryParams.pharmacy_service_type = checkFormValues.pharmacy_service_type;
    } else {
      delete checkFormValues["pharmacy_service_type"];
    }

    if (checkFormValues.prescriber_id || queryParams.prescriber_id) {
      queryParams.prescriber_id = checkFormValues.prescriber_id;
    } else {
      delete checkFormValues["prescriber_id"];
    }

    if (checkFormValues.prescriber_id_qualifier || queryParams.prescriber_id_qualifier) {
      queryParams.prescriber_id_qualifier = checkFormValues.prescriber_id_qualifier;
    } else {
      delete checkFormValues["prescriber_id_qualifier"];
    }

    if (checkFormValues.other_coverage_code || queryParams.other_coverage_code) {
      queryParams.other_coverage_code = checkFormValues.other_coverage_code;
    } else {
      delete checkFormValues["other_coverage_code"];
    }

    if (checkFormValues.other_payer_amount_paid_1 || queryParams.other_payer_amount_paid_1) {
      queryParams.other_payer_amount_paid_1 = checkFormValues.other_payer_amount_paid_1;
    } else {
      delete checkFormValues["other_payer_amount_paid_1"];
    }

    if (checkFormValues.other_payer_patient_responsibility_amount_1 || queryParams.other_payer_patient_responsibility_amount_1) {
      queryParams.other_payer_patient_responsibility_amount_1 = checkFormValues.other_payer_patient_responsibility_amount_1;
    } else {
      delete checkFormValues["other_payer_patient_responsibility_amount_1"];
    }

    if (checkFormValues.other_payer_reject_code_1 || queryParams.other_payer_reject_code_1) {
      queryParams.other_payer_reject_code_1 = checkFormValues.other_payer_reject_code_1;
    } else {
      delete checkFormValues["other_payer_reject_code_1"];
    }

    if (checkFormValues.special_packaging_indicator || queryParams.special_packaging_indicator) {
      queryParams.special_packaging_indicator = checkFormValues.special_packaging_indicator;
    } else {
      delete checkFormValues["special_packaging_indicator"];
    }

    if (checkFormValues.result_of_service_code || queryParams.result_of_service_code) {
      queryParams.result_of_service_code = checkFormValues.result_of_service_code;
    } else {
      delete checkFormValues["result_of_service_code"];
    }

    if (checkFormValues.reason_for_service_code || queryParams.reason_for_service_code) {
      queryParams.reason_for_service_code = checkFormValues.reason_for_service_code;
    } else {
      delete checkFormValues["reason_for_service_code"];
    }

    if (checkFormValues.dur_pps_level_of_effort || queryParams.dur_pps_level_of_effort) {
      queryParams.dur_pps_level_of_effort = checkFormValues.dur_pps_level_of_effort;
    } else {
      delete checkFormValues["dur_pps_level_of_effort"];
    }

    if (checkFormValues.professional_service_code || queryParams.professional_service_code) {
      queryParams.professional_service_code = checkFormValues.professional_service_code;
    } else {
      delete checkFormValues["professional_service_code"];
    }

    if (checkFormValues.prescription_origin_code || queryParams.prescription_origin_code) {
      queryParams.prescription_origin_code = checkFormValues.prescription_origin_code;
    } else {
      delete checkFormValues["prescription_origin_code"];
    }

    if (checkFormValues.notes || queryParams.notes) {
      queryParams.notes = checkFormValues.notes;
    } else {
      delete checkFormValues["notes"];
    }

    navigate(`/manual-claim?${queryString.convertJsonToQueryString(queryParams)}`);
  };


  const patientSummary = member_id ? (
    <PatientSummary
      sectionTitle="Search for and add a patient to the manual claim."
      path={"/manual-claim/patient" + search}
      buttonText="Add Patient"
      data-testid="add_patient"
      handleClick={() => handleAmplitudeLogging(ANALYTIC_EVENTS.MANUAL_CLAIM_PATIENT.removePatientFromManualClaim)}
    />
  ) : (
    <ManualClaimImageContainer
      sectionTitle="Search for and add a patient to the manual claim."
      sectionImage={<PatientSearchImage />}
      buttonText="Add Patient"
      path={"/manual-claim/patient" + search}
      handleClick={() => handleAmplitudeLogging(ANALYTIC_EVENTS.MANUAL_CLAIM_PATIENT.addPatientToManualClaim)}
    />
  );

  const pharmacySummary = npi ? (
    <PharmacySummary
      sectionTitle="Search for and add a pharmacy to the manual claim."
      path={"/manual-claim/pharmacy" + search}
      buttonText="Add Pharmacy"
      handleClick={() => handleAmplitudeLogging(ANALYTIC_EVENTS.MANUAL_CLAIM_PHARMACY.removePharmacyFromManualClaim)}
    />
  ) : (
    <ManualClaimImageContainer
      sectionTitle="Search for and add a pharmacy to the manual claim."
      sectionImage={<PharmacySearchImage />}
      buttonText="Add Pharmacy"
      path={"/manual-claim/pharmacy" + search}
      handleClick={() => handleAmplitudeLogging(ANALYTIC_EVENTS.MANUAL_CLAIM_PHARMACY.addPharmacyToManualClaim)}
    />
  );

  const drugTitle = Array.isArray(drugs) ? "Compound Drug" : "Single Drug";
  const drugSummary = drugs ? (
    <DrugSummary
      sectionTitle={drugTitle}
      path={"/manual-claim/drug" + search}
      buttonText="Add Drug"
      handleFieldOnBlur={handleOnBlur}
      selectedDMRIndicator={checkFormValues.direct_member_reimbursement_indicator}
    />
  ) : (
    <ManualClaimImageContainer
      sectionTitle="Search for and add drug(s) to the manual claim."
      sectionImage={<DrugSearchImage />}
      buttonText="Add Drug"
      path={"/manual-claim/drug" + search}
      handleClick={() => handleAmplitudeLogging(ANALYTIC_EVENTS.MANUAL_CLAIM_DRUG.addDrugFromManualClaim)}
    />
  );


  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ManualClaimSectionContainer readOnlyTheme={false} title="GENERAL INFORMATION">
        <GeneralClaimInformation handleFieldOnBlur={handleOnBlur} />
      </ManualClaimSectionContainer>
      <ManualClaimSectionContainer title="PATIENT">{patientSummary}</ManualClaimSectionContainer>
      <ManualClaimSectionContainer title="PHARMACY">{pharmacySummary}</ManualClaimSectionContainer>
      <ManualClaimSectionContainer title="DRUG">{drugSummary}</ManualClaimSectionContainer>
      <div className="row caprx-h-center-text">
        <div className="col">
          <SubmitBtn
            fullWidth={false}
            submitting={submitting}
            disabled={member_id && npi && drugs && date_of_service && claim_submission_date ? false : true}
          >
            Run
          </SubmitBtn>
        </div>
      </div>
    </form>
  );
};

export default reduxForm({ form: formName, enableReinitialize: true })(ManualClaimForm);
